import { useCookies } from 'react-cookie';

import { CartCookieSegment } from 'shared/types/cart';
import { environmentVariables } from 'shared/utils/environment.utils';
import useQuery from '../useQuery';
import { useSegment } from '../useSegment';

interface CartCookiePayload {
  'cart-details': string;
}
interface CartDetails {
  cartId: string;
  itemsCount: number;
  broadbandBundlePresent: boolean;
  segment: CartCookieSegment;
}

const CART_COOKIE_DOMAIN =
  environmentVariables.cartCookieName === 'mock-cart-cookie'
    ? 'localhost'
    : '.spark.co.nz';

// placeholder till we get the jsonwebtoken working properly
function parseJwt<TPayload>(token: string): TPayload {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        const internalString = `00${c.charCodeAt(0).toString(16)}`.slice(-2);
        return `%${internalString}`;
      })
      .join(''),
  );

  return JSON.parse(jsonPayload) as TPayload;
}

const DEFAULT_CART_DETAILS: Omit<CartDetails, 'segment'> = {
  cartId: '',
  itemsCount: 0,
  broadbandBundlePresent: false,
};

function decodeCartCookie(
  cartCookieValue: string,
  defaultSegment: CartCookieSegment,
): CartDetails {
  try {
    const payload = parseJwt<CartCookiePayload>(cartCookieValue);
    return JSON.parse(payload['cart-details']);
  } catch {
    return { ...DEFAULT_CART_DETAILS, segment: defaultSegment };
  }
}

interface UseCartCookieReturn {
  cartId: string;
  broadbandBundlePresent: boolean;
  expireCartCookie: () => void;
  clearCartCookie: () => void;
  numberOfCartItems: number;
  cartSegment: CartCookieSegment;
}

export default function useCartCookie(): UseCartCookieReturn {
  const { cartCookieName } = environmentVariables;
  const [cookies, , removeCookie] = useCookies();
  const { parseQueryValueString } = useQuery();
  const queryParameterCartId = parseQueryValueString('cartId');
  const { isBusiness } = useSegment();
  const defaultSegment = isBusiness ? 'Business' : 'Consumer';

  function expireCartCookie() {
    /*
      Use this function to clear the cookie without initiating any react lifecyle events.
      It sets the expiry of the cookie to the past which effectively deletes the cookie
    */
    document.cookie = `${cartCookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${CART_COOKIE_DOMAIN};`;
  }

  function clearCartCookie() {
    removeCookie(cartCookieName, { domain: CART_COOKIE_DOMAIN, path: '/' });
  }

  const {
    cartId: cartCookieCartId,
    itemsCount,
    segment,
    broadbandBundlePresent,
  } = decodeCartCookie(cookies[cartCookieName], defaultSegment);

  // override cart cookie if the cartId query parameter exists
  if (queryParameterCartId && queryParameterCartId !== cartCookieCartId) {
    return {
      cartId: queryParameterCartId,
      broadbandBundlePresent: DEFAULT_CART_DETAILS.broadbandBundlePresent,
      numberOfCartItems: DEFAULT_CART_DETAILS.itemsCount,
      cartSegment: defaultSegment,
      clearCartCookie,
      expireCartCookie,
    };
  }

  return {
    cartId: cartCookieCartId,
    broadbandBundlePresent,
    numberOfCartItems: itemsCount,
    cartSegment: segment,
    clearCartCookie,
    expireCartCookie,
  };
}
